import { GRCartBaseComponent } from "@theme/gr-cart-base";
import { DialogOpenEvent, DialogCloseEvent } from "./dialog.js";
import { isClickedOutside, onAnimationEnd } from "@theme/utilities";
import { initFreeGiftReconciler } from "./free-gift-manager.js";

/**
 * Cart Drawer Component. Cart behavior lives in GRCartBaseComponent; this
 * adds the dialog and the "refresh on open" path.
 *
 * The dialog methods are copied from DialogComponent (assets/dialog.js)
 * rather than inherited: the cart logic is the base class, and JS has single
 * inheritance. Wrapping the <dialog> in a nested <dialog-component> is not an
 * option either, because Component scopes `refs` to the nearest component
 * ancestor and the wrapper would claim every ref inside the dialog.
 *
 * @extends {GRCartBaseComponent}
 */
class GRCartDrawerComponent extends GRCartBaseComponent {
  requiredRefs = ["dialog"];

  /** @type {boolean} */
  #needsRefresh = true;

  /** @type {string | null} */
  #pendingSectionHtml = null;

  /** @type {number | undefined} */
  #safetyNetTimeout;

  /** @type {AbortController} */
  #abortController = new AbortController();

  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    // Custom events to open/close drawer
    document.addEventListener("gr-cart:open", this.#handleOpen, { signal });
    document.addEventListener("gr-cart:close", this.#handleClose, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    clearTimeout(this.#safetyNetTimeout);
    this.#safetyNetTimeout = undefined;
  }

  // ==========================================================================
  // Dialog (copied from DialogComponent)
  // ==========================================================================

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // When another dialog already locked the page, the body is `position: fixed`
    // and window.scrollY reads 0. Re-pinning from that would jerk the page to the
    // top (and restore to 0 on close). Inherit the real offset already stored in
    // body.style.top so a handoff between dialogs (e.g. added-to-cart -> gift
    // selection) keeps its scroll position. `dialog[open]` is the dialog-owned
    // signal that another dialog holds the lock.
    const lockedTop = parseFloat(document.body.style.top);
    const inheritLock =
      document.querySelector("dialog[open]") !== null &&
      Number.isFinite(lockedTop);
    const scrollY = inheritLock ? -lockedTop : window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      // Pin only if the body isn't already locked. Re-checked here (not just via
      // the synchronous inheritLock) so that if the other dialog happened to
      // unlock in the gap, we re-pin at the correct inherited offset rather than
      // leaving the page scrollable.
      if (document.body.style.position !== "fixed") {
        document.body.style.width = "100%";
        document.body.style.position = "fixed";
        document.body.style.top = `-${scrollY}px`;
      }

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener("click", this.#handleClick);
      this.addEventListener("keydown", this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    this.removeEventListener("click", this.#handleClick);
    this.removeEventListener("keydown", this.#handleKeyDown);

    dialog.classList.add("dialog-closing");

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    dialog.close();
    dialog.classList.remove("dialog-closing");

    // Only restore body scroll if no other dialogs are open
    const anyDialogOpen = document.querySelector("dialog[open]");
    if (!anyDialogOpen) {
      document.body.style.width = "";
      document.body.style.position = "";
      document.body.style.top = "";
      window.scrollTo({ top: this.#previousScrollY, behavior: "instant" });
    }

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick = (event) => {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown = (event) => {
    if (event.key !== "Escape") return;

    event.preventDefault();
    this.closeDialog();
  };

  // ==========================================================================
  // Drawer open / close
  // ==========================================================================

  /**
   * Opens the cart drawer
   */
  async open() {
    // Clear stale edit state when opening drawer outside the builder
    // Prevents spurious beforeunload dialogs (e.g. adding GWP from cart)
    if (
      typeof Alpine !== "undefined" &&
      !window.location.pathname.startsWith("/pages/custom-box")
    ) {
      const store = Alpine.store("customBox");
      if (store && store.editingCartItemKey) {
        store.editingCartItemKey = null;
        store.originalCuts = null;
        store.originalSellingPlanId = null;
        store.originalDeliveryFrequency = null;
        store.originalGwpVariantId = null;
        store.saveToStorage();
      }
    }

    // Refresh content if it was updated while closed

    const didMorph = this.#needsRefresh;
    if (this.#needsRefresh) {
      this.#pendingSectionHtml = null;
      await this._fetchAndMorphSection();
      this.#needsRefresh = false;
    }
    this.showDialog();

    if (!didMorph) {
      // Render bundle cuts on initial open (morph path handles its own render)
      this._renderAllBundleCuts();
    }

    // Safety net: re-render any empty bundle cuts containers after dialog shows.
    // Catches edge cases where open() render was skipped (didMorph path)
    // and the debounced post-morph render hasn't fired yet.
    clearTimeout(this.#safetyNetTimeout);
    this.#safetyNetTimeout = setTimeout(() => {
      this._renderAllBundleCuts({ onlyEmpty: true });
    }, 100);

    // Opening is the moment the shopper looks at the cart, so an open that did
    // not just re-render pays one forced `/cart.js` read and re-morphs in place
    // if the rows on screen no longer match (a change made in another window
    // that no tab-visibility or focus signal reported). After the refresh-on-
    // open morph the read stays stale-tolerant (ACQ-210 #1 STATE): the cart
    // only feeds the `custom_cart_viewed` payload and the promo message.
    const cart = await this._fetchCart(!didMorph);
    if (!didMorph && this._hasStaleCartMarkup(cart)) {
      await this._fetchAndMorphSection();
    }

    // Every morph above replaces the footer with its server-rendered (hidden)
    // state, so the eligibility message is re-derived after them.
    this._updatePromoEligibilityMessage(cart);

    if (Shopify?.analytics?.publish) {
      Shopify.analytics.publish("custom_cart_viewed", { data: cart });
    }
  }

  /**
   * Closes the cart drawer
   */
  close() {
    this.closeDialog();
  }

  #handleOpen = () => {
    this.open();
  };

  #handleClose = () => {
    this.close();
  };

  /**
   * Checkout on an empty cart just closes the drawer.
   */
  _onEmptyCheckout() {
    this.close();
  }

  /**
   * Drawer-specific cart update handling: only morph when the dialog is open,
   * otherwise mark for refresh on next open.
   * @param {import("./gr-cart-base.js").Cart | null} incomingCart
   * @param {string | undefined} sectionsHtml
   */
  async _applyExternalCartUpdate(incomingCart, sectionsHtml) {
    const isDialogOpen = this.refs.dialog?.open;

    // Only morph when dialog is open to prevent unexpected reopening
    if (isDialogOpen) {
      await super._applyExternalCartUpdate(incomingCart, sectionsHtml);
    } else {
      // Dialog is closed - mark that we need to refresh content when drawer opens next
      // Don't update cart bubble here - let cart-icon component handle it
      this.#needsRefresh = true;
      // Cache the section HTML from the Cart API response so we can use it
      // when the drawer opens, instead of relying on the Section Rendering API
      // which fails for section group sections on certain page URLs
      this.#pendingSectionHtml = sectionsHtml || null;
    }
  }
}

if (!customElements.get("gr-cart-drawer")) {
  customElements.define("gr-cart-drawer", GRCartDrawerComponent);
}

// The drawer module loads on every page, so this is the one registration
// point for the ACQ-299 free-gift reconciler (idempotent).
initFreeGiftReconciler();
